import { useMutation, useQueryClient } from '@tanstack/react-query';

import BackendService from '../services/backendService';
import { Template } from '../services/TemplateService/Template.type';
import useAccessBadge from './useAccessBadge';
import { useMetrics } from './useMetrics';
import { recentTemplatesCacheKey } from './useRecentTemplates';
import useSendSlackMessage from './useSubmitFeedback';

// Import the PromptVariables interface
interface PromptVariables {
  learningObjective: string;
  grade: string;
  subject?: string;
  languages?: string[];
  lessonCategory?:
    | 'teachSomethingNew'
    | 'reviewOrReteach'
    | 'practiceASkill'
    | 'readAloud'
    | 'closeReading'
    | 'other';
  standardAlignment?: string;
}

const useConsumeGenericGeneratorAsNewPresentation = (tenantId: string) => {
  const metrics = useMetrics();
  const client = useQueryClient();
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const { submit } = useSendSlackMessage();
  const presetState = userAccessBadge?.organization?.region;
  const { mutate: consumeGenerator, isLoading, data, isError, error } = useMutation(
    async (params: {
      // New signature
      promptVariables?: PromptVariables;
      // Old signature (deprecated)
      prompt?: string;
      grade?: string;
      subject?: string;
      fileType?: string;
      filePath?: string;
      isRetry?: boolean;
      languages?: string[];
      lessonCategory?: string;
      onSuccessFunction?: (template: Template) => void;
    }) => {
      const { promptVariables, ...oldParams } = params;

      // Use new signature if promptVariables is provided
      if (promptVariables) {
        metrics.logEvent('Generator.Consume', {
          prompt: promptVariables.learningObjective,
          grade: promptVariables.grade,
          subject: promptVariables.subject,
          generatorId: oldParams.filePath
            ? 'genericFullLessonCurify'
            : 'genericFullLesson',
          generatorName: oldParams.filePath
            ? 'genericFullLessonCurify'
            : 'genericFullLesson',
          fileType: oldParams.fileType,
          schoolState: presetState,
          isRetry: oldParams.isRetry,
          languages: promptVariables.languages,
          language: promptVariables.languages?.[0],
          isPresentation: true,
        });
        metrics.logEvent('Generator.Consume.genericFullLesson', {
          prompt: promptVariables.learningObjective,
          grade: promptVariables.grade,
          subject: promptVariables.subject,
          generatorId: oldParams.filePath
            ? 'genericFullLessonCurify'
            : 'genericFullLesson',
          generatorName: oldParams.filePath
            ? 'genericFullLessonCurify'
            : 'genericFullLesson',
          fileType: oldParams.fileType,
          schoolState: presetState,
          isRetry: oldParams.isRetry,
          languages: promptVariables.languages,
          language: promptVariables.languages?.[0],
          isPresentation: true,
        });

        const res = await BackendService.instance.ai.createFullLesson(
          {
            learningObjective: promptVariables.learningObjective,
            grade: promptVariables.grade,
            subject: promptVariables.subject,
            languages: promptVariables.languages,
            lessonCategory: promptVariables.lessonCategory,
            standardAlignment: promptVariables.standardAlignment,
            fileType: oldParams.fileType || null,
            filePath: oldParams.filePath || null,
          },
          tenantId,
        );
        return res;
      } else {
        // Use old signature (deprecated)
        console.warn(
          'Using deprecated consumeGenerator signature. Please migrate to use promptVariables.',
        );

        const {
          prompt,
          grade,
          subject,
          fileType,
          filePath,
          isRetry,
          languages,
          lessonCategory,
        } = oldParams;

        if (!prompt || !grade) {
          throw new Error('prompt and grade are required when using the old signature');
        }

        metrics.logEvent('Generator.Consume', {
          prompt,
          grade,
          subject,
          generatorId: filePath ? 'genericFullLessonCurify' : 'genericFullLesson',
          generatorName: filePath ? 'genericFullLessonCurify' : 'genericFullLesson',
          fileType,
          schoolState: presetState,
          isRetry,
          languages: languages,
          language: languages?.[0],
          isPresentation: true,
        });
        metrics.logEvent('Generator.Consume.genericFullLesson', {
          prompt,
          grade,
          subject,
          generatorId: filePath ? 'genericFullLessonCurify' : 'genericFullLesson',
          generatorName: filePath ? 'genericFullLessonCurify' : 'genericFullLesson',
          fileType,
          schoolState: presetState,
          isRetry,
          languages: languages,
          language: languages?.[0],
          isPresentation: true,
        });

        const res = await BackendService.instance.ai.createDynamicLesson(
          {
            prompt,
            grade,
            subject: subject || null,
            fileType: fileType || null,
            filePath: filePath || null,
            languages: languages || ['english'],
            lessonCategory: lessonCategory || null,
          },
          tenantId,
        );
        return res;
      }
    },
    {
      onSuccess: (data, { onSuccessFunction, promptVariables }) => {
        client.invalidateQueries([recentTemplatesCacheKey]);

        // Track successful lesson generation and landing on designer
        if (promptVariables) {
          metrics.logEvent('Generator.Success.genericFullLesson', {
            lessonId: data.id,
            lessonTitle: data.title,
            promptVariables: {
              learningObjective: promptVariables.learningObjective,
              grade: promptVariables.grade,
              subject: promptVariables.subject,
              languages: promptVariables.languages,
              lessonCategory: promptVariables.lessonCategory,
              standardAlignment: promptVariables.standardAlignment,
            },
            // Track all metadata about the successfully generated lesson
            lessonMetadata: {
              learningObjective: promptVariables.learningObjective,
              grade: promptVariables.grade,
              subject: promptVariables.subject,
              languages: promptVariables.languages,
              lessonCategory: promptVariables.lessonCategory,
              standardAlignment: promptVariables.standardAlignment,
              lessonId: data.id,
              lessonTitle: data.title,
              isPresentation: true,
              generatorId: 'genericFullLesson',
              generatorName: 'genericFullLesson',
            },
          });
        }

        if (onSuccessFunction && data) {
          onSuccessFunction(data);
        }
        return true;
      },
      cacheTime: 1000 * 60 * 5,

      onError: (error: any, { promptVariables }) => {
        // Track lesson generation errors
        if (promptVariables) {
          metrics.logEvent('Generator.Error.genericFullLesson', {
            errorMessage: error.customMessage || error.message,
            errorStatus: error.response?.status,
            errorCode: error.code,
            promptVariables: {
              learningObjective: promptVariables.learningObjective,
              grade: promptVariables.grade,
              subject: promptVariables.subject,
              languages: promptVariables.languages,
              lessonCategory: promptVariables.lessonCategory,
              standardAlignment: promptVariables.standardAlignment,
            },
            // Track error metadata
            errorMetadata: {
              errorMessage: error.customMessage || error.message,
              errorStatus: error.response?.status,
              errorCode: error.code,
              learningObjective: promptVariables.learningObjective,
              grade: promptVariables.grade,
              subject: promptVariables.subject,
              languages: promptVariables.languages,
              lessonCategory: promptVariables.lessonCategory,
              standardAlignment: promptVariables.standardAlignment,
              generatorId: 'genericFullLesson',
              generatorName: 'genericFullLesson',
            },
          });
        }

        submit({
          message: `\n*Error consuming full lesson or curify:* ${error.customMessage}\n*Error message:* ${error.message}\n*Error status:* ${error.response?.status}\nError code: ${error.code}`,
          accepted: false,
        });
      },
    },
  );

  return { consumeGenerator, isLoading, isError, data, error: error as any };
};

export default useConsumeGenericGeneratorAsNewPresentation;
